import React, { useEffect, useMemo, useRef, useState } from 'react';
import CodeMirror, { type ReactCodeMirrorRef } from '@uiw/react-codemirror';
import { autocompletion, type Completion, type CompletionContext } from '@codemirror/autocomplete';
import { StreamLanguage } from '@codemirror/language';
import { linter, type Diagnostic } from '@codemirror/lint';
import { EditorView, hoverTooltip } from '@codemirror/view';
import { Check, ChevronDown, ChevronRight, Copy, Info, Play, RotateCcw, Search, Table2, Variable } from 'lucide-react';
import { DialogShell, ghostBtn, primaryBtn, surfaces } from '../components/dataModelsUi';
import { logDataModels } from '../dataModelsLogger';
import { userContext } from './catalog';
import { resolveDirectoryUserId } from './policyDirectory';
import { usePolicyDirectory } from './usePolicyDirectory';
import {
  extractWhereClause,
  parseCatalogSql,
  seedCatalogQuery,
  type CatalogQueryResult,
} from './catalogQuery';
import { validateExpression } from './parser';
import { assertReadonlySql, liveQueryFail, resolveLiveAsset, rewriteLiveSql, substituteClaimTokens } from './queryBuilderLive';
import { userById } from './queryTest';
import { runAssetQuery } from '../tablePreviewApi';
import type { PolicyAsset, SyntheticUser, VariableMeta } from './types';
import { columnQualifiedName } from './usePolicyAssetColumns';
import { ALL_VARIABLES, FUNCTION_REGISTRY, USER_VARIABLES } from './variables';

const SQL_KEYWORDS = [
  { label: 'SELECT', type: 'keyword', detail: 'Columns to return' },
  { label: 'FROM', type: 'keyword', detail: 'Source table' },
  { label: 'WHERE', type: 'keyword', detail: 'Row filter' },
  { label: 'LIMIT', type: 'keyword', detail: 'Max sample rows' },
  { label: 'AND', type: 'keyword', detail: 'Logical AND' },
  { label: 'OR', type: 'keyword', detail: 'Logical OR' },
  { label: 'NOT', type: 'keyword', detail: 'Logical NOT' },
  { label: 'IN', type: 'keyword', detail: 'Membership' },
  { label: 'NOT IN', type: 'keyword', detail: 'Negated membership' },
  { label: 'IS NULL', type: 'keyword', detail: 'Null check' },
  { label: 'IS NOT NULL', type: 'keyword', detail: 'Not-null check' },
  { label: '=', type: 'operator', detail: 'Equals' },
  { label: '!=', type: 'operator', detail: 'Not equals' },
  { label: '>', type: 'operator', detail: 'Greater than' },
  { label: '<', type: 'operator', detail: 'Less than' },
  { label: '>=', type: 'operator', detail: 'Greater than or equal' },
  { label: '<=', type: 'operator', detail: 'Less than or equal' },
];

const SQL_LANGUAGE = StreamLanguage.define({
  token(stream) {
    if (stream.eatSpace()) return null;
    if (stream.match('${')) {
      stream.eatWhile(/[^}]/);
      stream.eat('}');
      return 'variableName';
    }
    if (stream.match(/'(?:[^'\\]|\\.)*'/)) return 'string';
    if (stream.match(/"(?:[^"\\]|\\.)*"/)) return 'string';
    if (stream.match(/^-?\d+(\.\d+)?/)) return 'number';
    if (stream.match(/^(SELECT|FROM|WHERE|LIMIT|AND|OR|NOT|IN|IS|NULL)\b/i)) return 'keyword';
    if (stream.match(/^(LOWER|UPPER|COALESCE|STARTS_WITH|ENDS_WITH|CONTAINS|CURRENT_DATE|CURRENT_TIMESTAMP)\b/i)) return 'atom';
    if (stream.match(/^[A-Za-z_][A-Za-z0-9_.]*/)) return 'propertyName';
    if (stream.match(/^(!=|>=|<=|=|>|<)/)) return 'operator';
    stream.next();
    return null;
  },
});

function uniqueColumns(assets: PolicyAsset[]) {
  const map = new Map<string, { name: string; type: string; classification?: string; assets: string[] }>();
  assets.forEach((asset) => {
    asset.columns.forEach((column) => {
      const current = map.get(column.name) ?? { name: column.name, type: column.type, classification: column.classification, assets: [] };
      current.assets.push(asset.name);
      map.set(column.name, current);
    });
  });
  return Array.from(map.values());
}

function completions(assets: PolicyAsset[]) {
  return (context: CompletionContext) => {
    const before = context.state.sliceDoc(0, context.pos);
    const variableOpen = before.lastIndexOf('${');
    const variableClose = before.lastIndexOf('}');
    if (variableOpen >= 0 && variableOpen > variableClose) {
      const typed = before.slice(variableOpen + 2);
      const options: Completion[] = ALL_VARIABLES.filter((item) => item.path.startsWith(typed) || item.path.split('.').pop()?.startsWith(typed))
        .map((item) => ({
          label: item.path,
          type: 'variable',
          detail: `${item.type} — ${item.example}`,
          info: item.description,
          apply: `${item.path}}`,
        }));
      return { from: variableOpen + 2, options, validFor: /[\w.]*/ };
    }

    const word = context.matchBefore(/[A-Za-z_$.][\w.]*/);
    if (!word && !context.explicit) return null;
    const query = (word?.text ?? '').toLowerCase();
    const tables = assets.map((asset) => ({
      label: asset.name,
      type: 'class',
      detail: `${asset.columns.length} columns · ${asset.rows.length} sample rows`,
      info: asset.classification ? `${asset.connectionType} · ${asset.classification}` : asset.connectionType,
    }));
    const columns = assets.flatMap((asset) => asset.columns.map((column) => ({
      label: columnQualifiedName(asset, column.name),
      type: 'property' as const,
      detail: `${column.type}${column.classification ? ` · ${column.classification}` : ''}`,
      info: asset.path || asset.name,
      apply: columnQualifiedName(asset, column.name),
    })));
    const functions = FUNCTION_REGISTRY.map((item) => ({
      label: item.name,
      type: 'function',
      detail: item.signature,
      info: item.description,
      apply: item.name.startsWith('CURRENT') ? item.name : `${item.name}()`,
    }));
    const variables = ALL_VARIABLES.map((item) => ({
      label: `\${${item.path}}`,
      type: 'variable',
      detail: `${item.type} — ${item.example}`,
      info: item.description,
    }));
    const options = [...SQL_KEYWORDS, ...tables, ...columns, ...functions, ...variables].filter((item) => item.label.toLowerCase().includes(query));
    return { from: word?.from ?? context.pos, options, validFor: /^[\w.${}]*$/ };
  };
}

function formatCell(value: unknown): string {
  if (value == null) return '—';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

type UserClaim = {
  path: string;
  token: string;
  value: string;
  original: string;
  faked: boolean;
  type: VariableMeta['type'];
  description: string;
};

function formatClaimValue(value: unknown): string {
  if (value == null) return '';
  if (Array.isArray(value)) return value.map((item) => String(item)).filter(Boolean).join(', ');
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  return String(value).trim();
}

function claimsForUser(user: SyntheticUser | undefined, overrides: Record<string, string> = {}): UserClaim[] {
  if (!user) return [];
  const ctx = userContext(user);
  return USER_VARIABLES.map((item) => {
    const original = formatClaimValue(ctx[item.path]);
    const faked = Object.prototype.hasOwnProperty.call(overrides, item.path);
    return {
      path: item.path,
      token: `\${${item.path}}`,
      original,
      value: faked ? overrides[item.path] : original,
      faked,
      type: item.type,
      description: item.description,
    };
  });
}

function claimsContext(user: SyntheticUser, overrides: Record<string, string>): Record<string, unknown> {
  const ctx: Record<string, unknown> = { ...userContext(user) };
  Object.entries(overrides).forEach(([path, raw]) => {
    const meta = USER_VARIABLES.find((item) => item.path === path);
    if (meta?.type === 'STRING[]') {
      ctx[path] = raw.split(',').map((part) => part.trim()).filter(Boolean);
      return;
    }
    if (meta?.type === 'BOOLEAN') {
      ctx[path] = /^(true|1|yes)$/i.test(raw.trim());
      return;
    }
    if (meta?.type === 'NUMBER') {
      const numeric = Number(raw);
      ctx[path] = Number.isFinite(numeric) ? numeric : raw;
      return;
    }
    ctx[path] = raw;
  });
  return ctx;
}

function userOptionLabel(user: SyntheticUser): string {
  const extra = user.department || user.role;
  return extra ? `${user.name} · ${extra}` : user.name;
}

function insertAtCursor(view: EditorView | undefined, text: string, fallback: (next: string) => void, current: string) {
  if (!view) {
    fallback(`${current}${current && !current.endsWith(' ') ? ' ' : ''}${text}`);
    return;
  }
  const { from, to } = view.state.selection.main;
  view.dispatch({
    changes: { from, to, insert: text },
    selection: { anchor: from + text.length },
  });
  view.focus();
}

function SimUserPicker({
  isLight,
  users,
  value,
  loading,
  onChange,
}: {
  isLight: boolean;
  users: SyntheticUser[];
  value: string;
  loading?: boolean;
  onChange: (id: string) => void;
}) {
  const boxRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const selected = users.find((user) => user.id === value);
  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return users;
    return users.filter((user) => {
      const haystack = [user.name, user.email, user.department, user.role, user.roles.join(' '), user.groups.join(' ')].join(' ').toLowerCase();
      return haystack.includes(needle);
    });
  }, [query, users]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (event: MouseEvent) => {
      if (!boxRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const field = isLight
    ? 'border-[#D8DCE2] bg-white text-[#1C1C1C] focus:border-[#1B6CA8]'
    : 'border-white/15 bg-black text-slate-100 focus:border-white/30';
  const muted = isLight ? 'text-[#6B7280]' : 'text-slate-400';
  const ink = isLight ? 'text-[#1C1C1C]' : 'text-white';
  const menu = isLight ? 'border-[#E6E8EB] bg-white shadow-lg' : 'border-white/10 bg-[#111] shadow-2xl';
  const hover = isLight ? 'hover:bg-[#F7F8FA]' : 'hover:bg-white/5';
  const active = isLight ? 'bg-[#E7F2FB] text-[#16324F]' : 'bg-white/10 text-white';

  return (
    <div ref={boxRef} className="relative">
      <button
        type="button"
        className={`flex h-7 w-[13.5rem] items-center justify-between gap-1.5 rounded-md border px-2 text-left text-[12px] focus:outline-none ${field}`}
        aria-label="Run as user"
        aria-expanded={open}
        aria-haspopup="listbox"
        disabled={!users.length && !loading}
        onClick={() => setOpen((current) => !current)}
      >
        <span className={`min-w-0 truncate ${selected ? ink : muted}`}>
          {selected ? userOptionLabel(selected) : loading ? 'Loading people…' : 'No people'}
        </span>
        <ChevronDown size={13} className={`shrink-0 ${muted}`} />
      </button>
      {open ? (
        <div className={`absolute right-0 z-30 mt-1 w-[18rem] overflow-hidden rounded-md border ${menu}`}>
          <div className={`border-b px-2 py-1.5 ${isLight ? 'border-[#E6E8EB]' : 'border-white/10'}`}>
            <div className="relative">
              <Search size={12} className={`pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 ${muted}`} />
              <input
                autoFocus
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search people"
                className={`h-7 w-full rounded-md border py-0 pl-7 pr-2 text-[12px] focus:outline-none ${field}`}
                aria-label="Search people"
              />
            </div>
          </div>
          <ul role="listbox" className="max-h-56 overflow-y-auto py-1">
            {filtered.map((user) => {
              const isActive = user.id === value;
              return (
                <li key={user.id}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={isActive}
                    className={`flex w-full flex-col items-start px-2.5 py-1.5 text-left ${isActive ? active : `${ink} ${hover}`}`}
                    onClick={() => {
                      onChange(user.id);
                      setOpen(false);
                      setQuery('');
                    }}
                  >
                    <span className="truncate text-[12px] font-medium">{user.name}</span>
                    <span className={`truncate text-[10px] ${muted}`}>
                      {[user.email, user.department || user.role].filter(Boolean).join(' · ') || user.id}
                    </span>
                  </button>
                </li>
              );
            })}
            {!filtered.length ? <li className={`px-2.5 py-3 text-[12px] ${muted}`}>No people match.</li> : null}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

function UserClaimsMenu({
  isLight,
  user,
  claims,
  copiedToken,
  onCopy,
  onInsert,
  onFake,
  onReset,
}: {
  isLight: boolean;
  user?: SyntheticUser;
  claims: UserClaim[];
  copiedToken: string;
  onCopy: (claim: UserClaim) => void;
  onInsert: (claim: UserClaim) => void;
  onFake: (path: string, value: string) => void;
  onReset: (path: string) => void;
}) {
  const boxRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const muted = isLight ? 'text-[#6B7280]' : 'text-slate-400';
  const ink = isLight ? 'text-[#1C1C1C]' : 'text-white';
  const menu = isLight ? 'border-[#E6E8EB] bg-white shadow-lg' : 'border-white/10 bg-[#111] shadow-2xl';
  const fakedCount = claims.filter((claim) => claim.faked).length;
  const trigger = isLight
    ? 'border-[#D8DCE2] bg-white text-[#1B6CA8] hover:border-[#1B6CA8]/50 hover:bg-[#F7FBFE]'
    : 'border-white/15 bg-white/[0.04] text-sky-300 hover:border-white/30 hover:bg-white/[0.08]';

  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (event: MouseEvent) => {
      if (!boxRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={boxRef} className="relative">
      <button
        type="button"
        className={`relative inline-flex h-7 items-center gap-1 rounded-md border px-1.5 focus:outline-none ${trigger}`}
        aria-label="Show user claims"
        aria-expanded={open}
        title="User claims"
        onClick={() => {
          setOpen((current) => !current);
          logDataModels('app', 'governance_query_builder_claims_toggle', { open: !open, userId: user?.id, claims: claims.length });
        }}
      >
        <Variable size={13} />
        <span className="font-mono text-[11px] font-semibold leading-none">${'{}'}</span>
        {fakedCount ? (
          <span className="absolute -right-1 -top-1 flex h-3.5 min-w-3.5 items-center justify-center rounded-full bg-[#1B6CA8] px-1 text-[8px] font-semibold text-white">
            {fakedCount}
          </span>
        ) : null}
      </button>
      {open ? (
        <div className={`absolute right-0 z-30 mt-1 w-[24rem] overflow-hidden rounded-md border ${menu}`}>
          <div className={`border-b px-3 py-2 ${isLight ? 'border-[#E6E8EB] bg-[#F7F8FA]' : 'border-white/10 bg-white/[0.03]'}`}>
            <p className={`text-[12px] font-medium ${ink}`}>
              {user ? `Claims · ${user.name}` : 'Claims'}
            </p>
            <p className={`mt-0.5 text-[11px] ${muted}`}>
              {user
                ? 'Edit a value and tick to fake it for Run. Reset restores the person.'
                : 'Select a person to see ${user.*} claims'}
            </p>
          </div>
          <ul className="max-h-72 overflow-y-auto py-1">
            {claims.map((claim) => (
              <ClaimEditRow
                key={claim.path}
                claim={claim}
                isLight={isLight}
                copied={copiedToken === claim.token}
                onCopy={() => onCopy(claim)}
                onInsert={() => onInsert(claim)}
                onFake={(value) => onFake(claim.path, value)}
                onReset={() => onReset(claim.path)}
              />
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

function ClaimEditRow({
  claim,
  isLight,
  copied,
  onCopy,
  onInsert,
  onFake,
  onReset,
}: {
  claim: UserClaim;
  isLight: boolean;
  copied: boolean;
  onCopy: () => void;
  onInsert: () => void;
  onFake: (value: string) => void;
  onReset: () => void;
}) {
  const [draft, setDraft] = useState(claim.value);
  const muted = isLight ? 'text-[#6B7280]' : 'text-slate-400';
  const ink = isLight ? 'text-[#1C1C1C]' : 'text-white';
  const token = isLight ? 'text-[#1B6CA8]' : 'text-sky-300';
  const field = isLight
    ? 'border-[#D8DCE2] bg-white text-[#1C1C1C] focus:border-[#1B6CA8]'
    : 'border-white/15 bg-black text-slate-100 focus:border-white/30';
  const dirty = draft !== claim.value;

  useEffect(() => {
    setDraft(claim.value);
  }, [claim.path, claim.value]);

  return (
    <li className={`border-b px-3 py-2 last:border-b-0 ${isLight ? 'border-[#F0F2F5]' : 'border-white/5'}`}>
      <div className="mb-1 flex items-center justify-between gap-2">
        <button
          type="button"
          className={`min-w-0 truncate text-left font-mono text-[12px] ${token}`}
          title={`Insert ${claim.token}`}
          onClick={onInsert}
        >
          {claim.token}
        </button>
        <div className="flex shrink-0 items-center gap-0.5">
          {claim.faked ? (
            <span className={`rounded-full px-1.5 py-px text-[9px] font-medium uppercase ${isLight ? 'bg-[#EEF6FC] text-[#1B6CA8]' : 'bg-sky-400/15 text-sky-200'}`}>
              Fake
            </span>
          ) : null}
          <button
            type="button"
            className={`rounded p-1 ${copied ? 'text-emerald-600' : muted}`}
            aria-label={copied ? `${claim.token} copied` : `Copy ${claim.token}`}
            title={copied ? 'Copied' : `Copy ${claim.token}`}
            onClick={onCopy}
          >
            {copied ? <Check size={13} /> : <Copy size={13} />}
          </button>
        </div>
      </div>
      <div className="flex items-center gap-1">
        <input
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              onFake(draft);
            }
          }}
          placeholder={claim.original || claim.description}
          aria-label={`Fake ${claim.token}`}
          className={`h-7 min-w-0 flex-1 rounded-md border px-2 font-mono text-[11px] focus:outline-none ${field} ${ink}`}
        />
        <button
          type="button"
          className={`inline-flex h-7 w-7 items-center justify-center rounded-md ${
            dirty || claim.faked ? 'bg-emerald-600 text-white hover:bg-emerald-700' : muted
          }`}
          aria-label={`Apply fake ${claim.token}`}
          title="Apply fake value"
          disabled={!dirty && !claim.faked}
          onClick={() => onFake(draft)}
        >
          <Check size={13} />
        </button>
        <button
          type="button"
          className={`inline-flex h-7 w-7 items-center justify-center rounded-md ${claim.faked || dirty ? muted : `${muted} opacity-40`}`}
          aria-label={`Reset ${claim.token}`}
          title="Reset to this person"
          disabled={!claim.faked && !dirty}
          onClick={() => {
            setDraft(claim.original);
            onReset();
          }}
        >
          <RotateCcw size={12} />
        </button>
      </div>
    </li>
  );
}

export default function QueryBuilderDialog({
  isLight,
  assets,
  expression,
  userId,
  onUserId,
  readOnly,
  onClose,
  onApplyFilter,
}: {
  isLight: boolean;
  assets: PolicyAsset[];
  expression: string;
  userId?: string;
  onUserId?: (id: string) => void;
  readOnly?: boolean;
  onClose: () => void;
  onApplyFilter: (where: string) => void;
}) {
  const ui = surfaces(isLight);
  const editorRef = useRef<ReactCodeMirrorRef>(null);
  const [sql, setSql] = useState(() => seedCatalogQuery(expression, assets));
  const directory = usePolicyDirectory();
  const [localUser, setLocalUser] = useState(userId ?? '');
  const [schemaQuery, setSchemaQuery] = useState('');
  const [openTables, setOpenTables] = useState<Record<string, boolean>>(() => ({ [assets[0]?.id ?? '']: true }));
  const [result, setResult] = useState<CatalogQueryResult | null>(null);
  const [running, setRunning] = useState(false);
  const [copiedToken, setCopiedToken] = useState('');
  const [claimOverrides, setClaimOverrides] = useState<Record<string, string>>({});
  const selectedUserId = userId ?? localUser;
  const setSelectedUserId = onUserId ?? setLocalUser;
  const selectedUser = userById(selectedUserId, directory.simUsers);
  const userClaims = useMemo(() => claimsForUser(selectedUser, claimOverrides), [claimOverrides, selectedUser]);
  const whereClause = extractWhereClause(sql);
  const muted = ui.muted;
  const ink = isLight ? 'text-[#1C1C1C]' : 'text-white';
  const line = ui.border;
  const filteredAssets = useMemo(() => {
    const query = schemaQuery.trim().toLowerCase();
    if (!query) return assets;
    return assets.flatMap((asset) => {
      const nameHit = asset.name.toLowerCase().includes(query);
      const columns = nameHit
        ? asset.columns
        : asset.columns.filter((column) => column.name.toLowerCase().includes(query) || (column.classification ?? '').toLowerCase().includes(query));
      if (!nameHit && !columns.length) return [];
      return [{ ...asset, columns }];
    });
  }, [assets, schemaQuery]);

  const extensions = useMemo(() => {
    const theme = EditorView.theme({
      '&': { fontSize: '12px', height: '100%' },
      '.cm-scroller': { overflow: 'auto' },
      '.cm-content': { minHeight: '140px', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace' },
      '.cm-tooltip': { borderRadius: '8px' },
    });
    const lint = linter((view) => {
      const text = view.state.doc.toString();
      const { parsed, errors } = parseCatalogSql(text);
      if (!parsed) {
        return errors.map((message): Diagnostic => ({ from: 0, to: Math.min(Math.max(text.length, 1), 12), severity: 'error', message }));
      }
      if (!parsed.where) return [];
      const whereMatch = text.match(/\bWHERE\b/i);
      const afterWhere = whereMatch && whereMatch.index != null ? text.slice(whereMatch.index + whereMatch[0].length) : parsed.where;
      const leading = afterWhere.length - afterWhere.trimStart().length;
      const offset = (whereMatch?.index ?? 0) + (whereMatch?.[0].length ?? 0) + leading;
      const result = validateExpression(parsed.where, assets);
      return result.issues.map((issue): Diagnostic => ({
        from: Math.min(offset + issue.from, view.state.doc.length),
        to: Math.min(Math.max(offset + issue.to, offset + issue.from + 1), view.state.doc.length),
        severity: issue.severity,
        message: issue.message,
      }));
    });
    const hover = hoverTooltip((view, pos) => {
      const word = view.state.wordAt(pos);
      if (!word) return null;
      const text = view.state.sliceDoc(word.from, word.to);
      const variable = ALL_VARIABLES.find((item) => item.path.endsWith(text) || item.path === text);
      const fn = FUNCTION_REGISTRY.find((item) => item.name === text.toUpperCase());
      const table = assets.find((item) => item.name === text);
      const column = uniqueColumns(assets).find((item) => item.name === text);
      const info = variable
        ? `${variable.path} — ${variable.type}\n${variable.description}\nExample: ${variable.example}`
        : fn
          ? `${fn.signature}\n${fn.description}`
          : table
            ? `${table.name} · ${table.columns.length} columns · ${table.rows.length} sample rows`
            : column
              ? `${column.name} — ${column.type}${column.classification ? ` · ${column.classification}` : ''}`
              : null;
      if (!info) return null;
      return {
        pos: word.from,
        end: word.to,
        create() {
          const dom = document.createElement('div');
          dom.style.whiteSpace = 'pre-wrap';
          dom.style.maxWidth = '280px';
          dom.style.padding = '6px 8px';
          dom.style.fontSize = '12px';
          dom.textContent = info;
          return { dom };
        },
      };
    });
    return [SQL_LANGUAGE, autocompletion({ override: [completions(assets)] }), lint, hover, theme, EditorView.lineWrapping];
  }, [assets]);

  const runLive = async (source: string, user: SyntheticUser, overrides = claimOverrides) => {
    const where = extractWhereClause(source);
    const writeError = assertReadonlySql(source);
    if (writeError) {
      logDataModels('warning', 'governance_query_builder_live_run', {
        ok: false,
        reason: 'write_sql',
        userId: user.id,
        claims: 0,
      });
      setResult(liveQueryFail(writeError, { where, errors: [writeError] }));
      return;
    }
    const resolved = resolveLiveAsset(source, assets);
    if (!resolved.asset || resolved.error) {
      logDataModels('warning', 'governance_query_builder_live_run', {
        ok: false,
        reason: 'asset',
        userId: user.id,
        claims: 0,
      });
      setResult(liveQueryFail(resolved.error || 'This table has no live connection.', { where }));
      return;
    }
    const substituted = substituteClaimTokens(source, claimsContext(user, overrides));
    if (substituted.error) {
      logDataModels('warning', 'governance_query_builder_live_run', {
        ok: false,
        reason: 'claims',
        userId: user.id,
        claims: substituted.tokens.length,
        sourceId: resolved.asset.sourceId,
      });
      setResult(liveQueryFail(substituted.error, { where }));
      return;
    }
    const liveSql = rewriteLiveSql(substituted.sql, assets);
    setRunning(true);
    logDataModels('app', 'governance_query_builder_live_run', {
      userId: user.id,
      claims: substituted.tokens.length,
      sourceId: resolved.asset.sourceId,
    });
    try {
      const payload = await runAssetQuery({
        sourceId: resolved.asset.sourceId || '',
        tableName: resolved.asset.name,
        path: resolved.asset.path,
        query: liveSql,
      });
      const columns = payload.columns.map((column) => column.name);
      setResult({
        ok: true,
        errors: [],
        columns,
        rows: payload.rows,
        matched: payload.rows.length,
        scanned: payload.rows.length,
        truncated: false,
        assetName: resolved.asset.name,
        where,
        explanation: `Live test as ${user.name}. Sent: ${liveSql}`,
      });
      logDataModels('app', 'governance_query_builder_live_run', {
        ok: true,
        userId: user.id,
        claims: substituted.tokens.length,
        sourceId: resolved.asset.sourceId,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to run live query';
      logDataModels('error', 'governance_query_builder_live_run', {
        ok: false,
        userId: user.id,
        claims: substituted.tokens.length,
        sourceId: resolved.asset.sourceId,
      });
      setResult(liveQueryFail(message, { where, errors: [message] }));
    } finally {
      setRunning(false);
    }
  };

  const run = (source = sql) => {
    const user = userById(selectedUserId, directory.simUsers);
    if (!user) {
      logDataModels('warning', 'governance_query_builder_live_run', { ok: false, reason: 'no_user', userId: selectedUserId });
      setResult(liveQueryFail('Select a person to run this live test.'));
      return;
    }
    void runLive(source, user);
  };

  useEffect(() => {
    const next = resolveDirectoryUserId(selectedUserId, directory.simUsers);
    if (next !== selectedUserId) setSelectedUserId(next);
  }, [directory.simUsers, selectedUserId, setSelectedUserId]);

  useEffect(() => {
    logDataModels('app', 'governance_query_builder_opened', { assets: assets.length, hasExpression: Boolean(expression.trim()) });
    // Open log only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setFromTable = (name: string) => {
    setSql((current) => {
      if (/\bFROM\s+[A-Za-z_][\w.]*/i.test(current)) return current.replace(/\bFROM\s+[A-Za-z_][\w.]*/i, `FROM ${name}`);
      return seedCatalogQuery('', assets.filter((asset) => asset.name === name));
    });
  };

  const insertColumn = (name: string) => {
    insertAtCursor(editorRef.current?.view, name, setSql, sql);
  };

  const chooseUser = (id: string) => {
    setSelectedUserId(id);
    setClaimOverrides({});
    const user = userById(id, directory.simUsers);
    logDataModels('app', 'governance_query_builder_user', { userId: id, claims: claimsForUser(user).length });
    if (user) void runLive(sql, user, {});
  };

  const fakeClaim = (path: string, value: string) => {
    setClaimOverrides((current) => ({ ...current, [path]: value }));
    logDataModels('app', 'governance_query_builder_claim_faked', { path, userId: selectedUserId });
  };

  const resetClaim = (path: string) => {
    setClaimOverrides((current) => {
      if (!Object.prototype.hasOwnProperty.call(current, path)) return current;
      const next = { ...current };
      delete next[path];
      return next;
    });
    logDataModels('app', 'governance_query_builder_claim_reset', { path, userId: selectedUserId });
  };

  const copyClaim = async (claim: UserClaim) => {
    try {
      await navigator.clipboard.writeText(claim.token);
      setCopiedToken(claim.token);
      window.setTimeout(() => setCopiedToken((current) => (current === claim.token ? '' : current)), 1600);
      logDataModels('app', 'governance_query_builder_claim_copied', { token: claim.token, userId: selectedUserId });
    } catch (err) {
      logDataModels('error', 'governance_query_builder_claim_copy_failed', {
        token: claim.token,
        message: err instanceof Error ? err.message : String(err),
      });
    }
  };

  const insertClaim = (claim: UserClaim) => {
    insertAtCursor(editorRef.current?.view, claim.token, setSql, sql);
    logDataModels('app', 'governance_query_builder_claim_inserted', { token: claim.token, userId: selectedUserId });
  };

  return (
    <DialogShell
      title="Query builder"
      subtitle="Write a read-only SELECT to test live. Use as filter copies the WHERE expression into the policy — not the whole query."
      isLight={isLight}
      onClose={onClose}
      size="full"
      footer={(
        <>
          <button type="button" className={ghostBtn(isLight)} onClick={onClose}>Close</button>
          <button
            type="button"
            className={primaryBtn()}
            disabled={readOnly || !whereClause}
            onClick={() => {
              onApplyFilter(whereClause);
              onClose();
              logDataModels('app', 'governance_query_builder_applied', { length: whereClause.length });
            }}
          >
            Use as filter
          </button>
        </>
      )}
    >
      <div className="flex h-[min(72vh,740px)] min-h-[520px] overflow-hidden">
        <aside className={`flex min-h-0 w-[260px] shrink-0 flex-col border-r ${line} ${isLight ? 'bg-[#F7F8FA]' : 'bg-white/[0.02]'}`}>
          <div className={`border-b px-3 py-2.5 ${line}`}>
            <p className={`mb-2 text-[11px] font-semibold uppercase tracking-wide ${muted}`}>Schema</p>
            <div className="relative">
              <Search size={13} className={`pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 ${muted}`} />
              <input
                value={schemaQuery}
                onChange={(event) => setSchemaQuery(event.target.value)}
                placeholder="Search tables and columns"
                className={`h-8 w-full rounded-md border py-0 pl-8 pr-2 text-[12px] focus:outline-none ${
                  isLight
                    ? 'border-[#D8DCE2] bg-white text-[#1C1C1C] placeholder:text-[#9AA1AB] focus:border-[#1B6CA8]'
                    : 'border-white/15 bg-black text-slate-100 placeholder:text-slate-500 focus:border-white/30'
                }`}
              />
            </div>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-2 py-2">
            {filteredAssets.map((asset) => {
              const open = openTables[asset.id] ?? Boolean(schemaQuery);
              return (
                <div key={asset.id} className="mb-1">
                  <div className="flex items-center gap-0.5">
                    <button
                      type="button"
                      className={`inline-flex h-6 w-6 items-center justify-center rounded ${muted}`}
                      aria-label={open ? `Collapse ${asset.name}` : `Expand ${asset.name}`}
                      onClick={() => setOpenTables((current) => ({ ...current, [asset.id]: !open }))}
                    >
                      <ChevronRight size={13} className={open ? 'rotate-90' : ''} />
                    </button>
                    <button
                      type="button"
                      className={`flex min-w-0 flex-1 items-center gap-1.5 rounded-md px-1.5 py-1 text-left text-[12px] ${isLight ? 'hover:bg-white' : 'hover:bg-white/5'}`}
                      onClick={() => setFromTable(asset.name)}
                    >
                      <Table2 size={12} className={muted} />
                      <span className={`truncate font-mono ${ink}`}>{asset.name}</span>
                    </button>
                  </div>
                  {open ? (
                    <ul className="mb-1 ml-6 space-y-0.5">
                      {asset.columns.map((column) => (
                        <li key={`${asset.id}:${column.name}`}>
                          <button
                            type="button"
                            className={`flex w-full items-center justify-between gap-2 rounded-md px-1.5 py-1 text-left ${isLight ? 'hover:bg-white' : 'hover:bg-white/5'}`}
                            onClick={() => insertColumn(columnQualifiedName(asset, column.name))}
                          >
                            <span className={`truncate font-mono text-[11px] ${ink}`} title={columnQualifiedName(asset, column.name)}>{column.name}</span>
                            <span className={`shrink-0 text-[10px] ${muted}`}>{column.classification ?? column.type}</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              );
            })}
            {!filteredAssets.length ? <p className={`px-2 py-3 text-[12px] ${muted}`}>No tables match this search.</p> : null}
          </div>
        </aside>

        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          <div className={`flex flex-wrap items-center justify-between gap-2 border-b px-3 py-2 ${line}`}>
            <p className={`text-[12px] ${muted}`}>Live test · tokens replaced from the selected person</p>
            <div className="flex flex-wrap items-center gap-1.5">
              <span className={`text-[11px] ${muted}`}>As</span>
              <SimUserPicker
                isLight={isLight}
                users={directory.simUsers}
                value={selectedUserId}
                loading={directory.loading}
                onChange={chooseUser}
              />
              <button
                type="button"
                className={primaryBtn()}
                disabled={running}
                onClick={() => run()}
              >
                <Play size={13} />
                {running ? 'Running…' : 'Run'}
              </button>
              <UserClaimsMenu
                isLight={isLight}
                user={selectedUser}
                claims={userClaims}
                copiedToken={copiedToken}
                onCopy={(claim) => { void copyClaim(claim); }}
                onInsert={insertClaim}
                onFake={fakeClaim}
                onReset={resetClaim}
              />
            </div>
          </div>
          <div className={`flex items-start gap-2 border-b px-3 py-2 ${line} ${isLight ? 'bg-[#F7FBFE]' : 'bg-white/[0.03]'}`}>
            <Info size={13} className={`mt-0.5 shrink-0 ${isLight ? 'text-[#1B6CA8]' : 'text-sky-300'}`} />
            <p className={`text-[11px] leading-4 ${muted}`}>
              Run tests the full SQL against live data. Use as filter / the policy only needs the expression (the WHERE clause), not the whole SELECT. Keep ${'{user.*}'} tokens in that expression.
            </p>
          </div>

          <div
            className={`h-[180px] shrink-0 border-b ${line}`}
            onKeyDown={(event) => {
              if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
                event.preventDefault();
                run();
              }
            }}
          >
            <CodeMirror
              ref={editorRef}
              value={sql}
              height="180px"
              theme={isLight ? 'light' : 'dark'}
              extensions={extensions}
              basicSetup={{ lineNumbers: true, foldGutter: false, highlightActiveLine: true }}
              onChange={setSql}
            />
          </div>

          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <div className={`flex items-center justify-between gap-2 border-b px-3 py-2 ${line}`}>
              <p className={`text-[12px] font-medium ${ink}`}>Results</p>
              <p className={`text-[11px] ${muted}`}>
                {result?.ok
                  ? `${result.rows.length} shown · ${result.matched} matched · ${result.scanned} scanned${result.truncated ? ' · truncated' : ''}`
                  : running
                    ? 'Running live test…'
                    : 'Run a query to preview live rows'}
              </p>
            </div>
            {result && !result.ok ? (
              <div className={`mx-3 mt-3 rounded-md border px-3 py-2 text-[12px] ${isLight ? 'border-red-200 bg-red-50 text-red-700' : 'border-red-500/30 bg-red-500/10 text-red-200'}`}>
                <p className="font-medium">Query failed</p>
                <p className="mt-0.5">{result.explanation}</p>
                {result.errors.map((error) => <p key={error}>{error}</p>)}
              </div>
            ) : null}
            {result?.ok ? (
              result.rows.length ? (
                <div className="min-h-0 flex-1 overflow-auto px-3 py-3">
                  <table className="min-w-full border-collapse text-left text-[12px]">
                    <thead className={`sticky top-0 ${ui.tableHead}`}>
                      <tr>
                        {result.columns.map((column) => (
                          <th key={column} className={`whitespace-nowrap border-b px-2 py-1.5 font-mono font-medium ${line}`}>{column}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {result.rows.map((row, index) => (
                        <tr key={`${result.assetName}-${index}`} className={index % 2 ? ui.rowAlt : ui.row}>
                          {result.columns.map((column) => {
                            const display = formatCell(row[column]);
                            return (
                              <td key={column} title={display} className={`max-w-[220px] truncate whitespace-nowrap border-b px-2 py-1.5 font-mono ${line} ${ink}`}>
                                {display}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className={`px-3 py-8 text-center text-[13px] ${muted}`}>No live rows match this filter for {userById(selectedUserId, directory.simUsers)?.name ?? 'this user'}.</p>
              )
            ) : null}
            {result?.ok ? <p className={`border-t px-3 py-2 text-[11px] ${line} ${muted}`}>{result.explanation}</p> : null}
          </div>
        </div>
      </div>
    </DialogShell>
  );
}
